// BL598 Phase 2 — `grid delete entity <slug>`.
//
// kubectl-noun replacement for the deleted top-level `grid delete`.
// Archives an inspiration (DELETE /api/v2/inspirations/:id). Apps and agents
// are not archived here — they have a richer lifecycle (dark / grace / hard)
// reached through `grid unplug`, so a runtime slug is pointed there instead.
//
// Confirmation by default; `--yes` skips the prompt for scripting.

import { Command } from 'commander';
import inquirer from 'inquirer';
import { getV2Client, type IV2Client } from '../../api/v2-client.js';
import { V2ApiError } from '../../api/v2-errors.js';
import { CloudGridError } from '../../utils.js';
import { handleV2Error } from '../../handle-error.js';

interface InspirationLookup {
  entity_id: string;
  name: string;
  display_name: string;
}

export interface DeleteEntityOptions {
  yes?: boolean;
}

/**
 * Distinguish "app/agent typed by mistake" from "nothing here". Only a 404
 * (RUNTIME_NOT_FOUND) means "not a runtime" — fall through to the
 * inspiration-not-found message. Anything else (5xx, 401, network) is a real
 * failure and must bubble so the user sees the actual error.
 */
async function slugResolvesToRuntime(client: IV2Client, slug: string): Promise<boolean> {
  try {
    await client.get(`/api/v2/runtimes/${encodeURIComponent(slug)}`);
    return true;
  } catch (err) {
    if (err instanceof V2ApiError && err.status === 404) {
      return false;
    }
    throw err;
  }
}

export async function runDeleteEntity(
  client: IV2Client,
  slug: string,
  opts: DeleteEntityOptions,
): Promise<void> {
  let inspiration: InspirationLookup;
  try {
    inspiration = await client.get<InspirationLookup>(
      `/api/v2/inspirations/${encodeURIComponent(slug)}`,
    );
  } catch (err) {
    if (err instanceof V2ApiError && err.status === 404) {
      if (await slugResolvesToRuntime(client, slug)) {
        throw new CloudGridError(
          [
            `'${slug}' is an app or agent, not an inspiration.`,
            `delete entity only archives inspirations. To remove it, run: grid unplug --hard ${slug}`,
          ].join('\n'),
        );
      }
      throw new CloudGridError(
        [
          `No inspiration named '${slug}' in your active grid.`,
          `If '${slug}' is an app or agent, remove it with: grid unplug --hard ${slug}`,
        ].join('\n'),
      );
    }
    throw err;
  }

  if (!opts.yes) {
    const { ok } = await inquirer.prompt([
      {
        type: 'confirm',
        name: 'ok',
        message: `Archive inspiration ${inspiration.name} ("${inspiration.display_name}")? Outlet returns 410 Gone after this.`,
        default: false,
      },
    ]);
    if (!ok) {
      process.stdout.write('Cancelled.\n');
      return;
    }
  }

  await client.del(`/api/v2/inspirations/${inspiration.entity_id}`);
  process.stdout.write(`Archived ${inspiration.name}.\n`);
}

export const deleteEntityCommand = new Command('entity')
  .description('Archive an inspiration (apps and agents use grid unplug)')
  .argument('<slug>', 'Inspiration slug')
  .option('--yes', 'Skip the confirmation prompt')
  .action(async (slug: string, opts: DeleteEntityOptions) => {
    try {
      const client = await getV2Client();
      await runDeleteEntity(client, slug, opts);
    } catch (err) {
      await handleV2Error(err);
    }
  });
